import Seo from "@/components/Seo";
import { PROJECTS } from "@/lib/constants";
import { MapPin, Camera, Calendar, ShieldCheck, FlaskConical, BookOpen, Award, Sparkles, Landmark, Building2, Home, Factory, GraduationCap, Trees, Gavel, ScrollText } from "lucide-react";

const EXPERTISE_PILLARS = [
  {
    icon: BookOpen,
    title: "Asbestos Subject Matter Experts",
    body: "Two decades of specialist knowledge across heritage, loose-fill (Mr Fluffy), customs, education and emergency-response work.",
    accent: "bg-[#0E2A5C] text-[#A6CD3C]",
  },
  {
    icon: FlaskConical,
    title: "Occupational Hygienists",
    body: "Independent, NATA-accredited air monitoring and exposure assessment led by qualified hygienists.",
    accent: "bg-[#A6CD3C] text-[#0E2A5C]",
  },
  {
    icon: ShieldCheck,
    title: "Hazmat Specialists",
    body: "Asbestos, lead, mould, soil contamination and combustion residues — full hazardous-material scope under one team.",
    accent: "bg-[#3B6CA8] text-white",
  },
];

const HERO_STATS = [
  { value: "50,000+", label: "Cases Investigated" },
  { value: "18+", label: "Years Operating" },
  { value: "200+", label: "Sites Per Year" },
  { value: "NATA", label: "Accredited Labs" },
];

const EXPERTISE_DOT = {
  "Hazmat Specialists": "bg-[#0E2A5C]",
  "Occupational Hygienists": "bg-[#A6CD3C]",
  "Asbestos Subject Matter Experts": "bg-[#3B6CA8]",
};

// Sectors served — broad SEO coverage + reinforces "no job too small"
const SECTORS = [
  { label: "Government", icon: Landmark },
  { label: "Cross-Government", icon: Gavel },
  { label: "Commercial", icon: Building2 },
  { label: "Public Institutions", icon: GraduationCap },
  { label: "Residential", icon: Home },
  { label: "Industrial", icon: Factory },
  { label: "Heritage & Aboriginal", icon: BookOpen },
  { label: "Environmental / Parks", icon: Trees },
];

// Compliance pillars — WHS Act, WHS Regulations, Codes of Practice, ISO
const COMPLIANCE = [
  { code: "WHS Act 2011", label: "Work Health & Safety Act", icon: Gavel },
  { code: "WHS Regulations", label: "Statutory Regulations", icon: ScrollText },
  { code: "Codes of Practice", label: "Industry Codes", icon: BookOpen },
  { code: "ISO Compliant", label: "International Standards", icon: ShieldCheck },
];

export default function Projects() {
  // JSON-LD structured data for AI + organic SEO
  const projectsSchema = {
    "@context": "https://schema.org",
    "@type": "ItemList",
    name: "ISGS Asbestos Consulting — Selected Case Studies",
    description:
      "Selected ISGS Asbestos Consulting case studies — Sydney Opera House heritage (2016), Fort Street Public School heritage & Aboriginal (2022), 2024 & 2025 fire-damage emergency hazmat responses (Kurnell, Forbes, Berowra Heights), Mr Fluffy loose-fill asbestos residential remediation (2023), Royal National Park illegal-dumping environmental response, and 1 Martin Place commercial CBD hazmat (2019). Drawn from 50,000+ investigations delivered over 18+ years across NSW and Australia-wide — WHS Act 2011, WHS Regulations, Codes of Practice and ISO-aligned.",
    itemListElement: PROJECTS.map((p, i) => ({
      "@type": "ListItem",
      position: i + 1,
      item: {
        "@type": "Article",
        headline: p.title,
        about: p.category,
        contentLocation: p.location,
        datePublished: `${p.year}-01-01`,
        author: { "@type": "Organization", name: "ISGS Consulting Group Pty Ltd" },
        publisher: { "@type": "Organization", name: "ISGS Consulting Group Pty Ltd" },
        keywords: p.services.join(", "),
        description: p.summary,
      },
    })),
  };

  return (
    <div data-testid="projects-page">
      <Seo
        title="Case Studies — 18+ Years, 50,000+ Asbestos & Hazmat Investigations | ISGS"
        description="ISGS Asbestos Consulting case studies — Sydney Opera House heritage (2016), Fort Street Public School heritage & Aboriginal (2022), Mr Fluffy loose-fill asbestos (2023), 2024 & 2025 fire-damage emergency hazmat response (Kurnell, Forbes, Berowra Heights), Royal National Park illegal-dumping environmental remediation and 1 Martin Place commercial CBD hazmat (2019). NATA-accredited, Class A/B assessors, WHS Act 2011, WHS Regulations, Codes of Practice and ISO-aligned. NSW and Australia-wide."
        path="/projects"
        schema={projectsSchema}
      />

      {/* ────────── HERO — editorial statement ────────── */}
      <section className="relative overflow-hidden bg-gradient-to-br from-white via-[#FAFCF3] to-[#EAF1F8] border-b border-[#0E2A5C]/8">
        {/* Dot grid */}
        <div
          className="absolute inset-0 opacity-30 pointer-events-none"
          style={{ backgroundImage: "radial-gradient(circle at 1px 1px, #0E2A5C 1.5px, transparent 0)", backgroundSize: "32px 32px" }}
          aria-hidden
        />
        {/* Colour orbs */}
        <div className="absolute -top-24 -left-16 w-[420px] h-[420px] rounded-full bg-[#A6CD3C]/25 blur-3xl pointer-events-none" aria-hidden />
        <div className="absolute -bottom-32 right-[-80px] w-[460px] h-[460px] rounded-full bg-[#3B6CA8]/20 blur-3xl pointer-events-none" aria-hidden />

        <div className="container-x relative z-10 pt-24 pb-20 grid lg:grid-cols-12 gap-12 items-center">
          <div className="lg:col-span-7">
            <div className="inline-flex items-center gap-2 bg-white border border-[#0E2A5C]/15 rounded-full px-3.5 py-1 mb-6 shadow-sm">
              <span className="w-1.5 h-1.5 rounded-full bg-[#A6CD3C]" />
              <span className="text-[11px] tracking-[0.22em] uppercase font-semibold text-[#0E2A5C]">Case Studies</span>
            </div>
            <div className="w-20 h-[3px] bg-[#A6CD3C] mb-6 rounded-full" />

            <h1 className="text-[44px] md:text-[64px] lg:text-[74px] font-bold leading-[0.98] text-[#0E2A5C] tracking-[-0.02em]">
              18 years.<br />
              <span className="relative inline-block">
                <span>50,000+ cases.</span>
                <span className="absolute inset-x-0 bottom-1 h-3 md:h-4 bg-[#A6CD3C]/55 -z-10" />
              </span><br />
              <span className="italic font-light text-[#5B6478]">One trusted partner.</span>
            </h1>

            <p className="mt-7 text-[#0B0F1A]/70 text-[16.5px] md:text-[18px] leading-relaxed max-w-2xl">
              ISGS Asbestos Consulting has spent almost two decades as Australia&apos;s independent subject-matter experts on asbestos and hazardous-material work — from Sydney Opera House heritage investigations to Mr Fluffy loose-fill, emergency fire-damage response, air monitoring and environmental remediation across NSW.
            </p>

            <div className="mt-8 flex flex-wrap items-center gap-x-6 gap-y-3 text-[12px] uppercase tracking-[0.22em] font-bold text-[#0E2A5C]/85">
              <span className="inline-flex items-center gap-2">
                <Award size={14} className="text-[#A6CD3C]" /> Subject Matter Experts
              </span>
              <span className="hidden sm:inline-block w-px h-3 bg-[#0E2A5C]/20" />
              <span className="inline-flex items-center gap-2">
                <ShieldCheck size={14} className="text-[#3B6CA8]" /> NATA-Accredited Labs
              </span>
              <span className="hidden sm:inline-block w-px h-3 bg-[#0E2A5C]/20" />
              <span className="inline-flex items-center gap-2">
                <Sparkles size={14} className="text-[#D89A3D]" /> Independent
              </span>
            </div>
          </div>

          {/* Stats card cluster */}
          <div className="lg:col-span-5">
            <div className="grid grid-cols-2 gap-4">
              {HERO_STATS.map((s, i) => (
                <div
                  key={s.label}
                  className={`relative overflow-hidden rounded-2xl p-6 lg:p-7 shadow-[0_20px_40px_-20px_rgba(14,42,92,0.35)] ${
                    i === 0 ? "bg-[#0E2A5C] text-white" :
                    i === 1 ? "bg-white text-[#0E2A5C] border border-[#0E2A5C]/10" :
                    i === 2 ? "bg-white text-[#0E2A5C] border border-[#0E2A5C]/10" :
                    "bg-[#A6CD3C] text-[#0E2A5C]"
                  }`}
                  data-testid={`hero-stat-${i}`}
                >
                  {i === 0 && (
                    <div className="absolute -right-6 -top-6 w-24 h-24 rounded-full bg-[#A6CD3C]/20 blur-2xl" />
                  )}
                  <div className="relative">
                    <div className={`text-[32px] lg:text-[38px] font-black leading-none tracking-tight`}>
                      {s.value}
                    </div>
                    <div className={`mt-3 text-[10.5px] uppercase tracking-[0.22em] font-bold ${i === 0 ? "text-white/75" : i === 3 ? "text-[#0E2A5C]/80" : "text-[#5B6478]"}`}>
                      {s.label}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* ────────── EXPERTISE STRIP — three disciplines ────────── */}
      <section className="section-pad bg-[#F7F8F3] border-b border-[#0E2A5C]/8" data-testid="expertise-pillars">
        <div className="container-x">
          <div className="grid lg:grid-cols-12 gap-10 lg:items-end mb-10">
            <div className="lg:col-span-7">
              <div className="eyebrow mb-3">Our expertise</div>
              <h2 className="text-[30px] md:text-[40px] font-bold text-[#0E2A5C] leading-tight">
                Three disciplines.{" "}
                <span className="font-light italic text-[#5B6478]">One integrated team.</span>
              </h2>
            </div>
            <p className="lg:col-span-5 text-[#5B6478] text-[15px] leading-relaxed">
              A single point of accountability across asbestos identification, occupational hygiene and full-spectrum hazmat consulting — backed by 18+ years and 50,000+ delivered engagements.
            </p>
          </div>

          <div className="grid md:grid-cols-3 gap-5">
            {EXPERTISE_PILLARS.map((p) => (
              <div
                key={p.title}
                className="bg-white rounded-2xl p-7 border border-[#0E2A5C]/10 hover:border-[#A6CD3C] transition-colors"
                data-testid={`pillar-${p.title.toLowerCase().replace(/\s+/g, "-")}`}
              >
                <div className={`w-12 h-12 rounded-xl ${p.accent} flex items-center justify-center mb-5`}>
                  <p.icon size={22} />
                </div>
                <h3 className="text-[18px] font-semibold text-[#0E2A5C] mb-2">{p.title}</h3>
                <p className="text-[14px] text-[#5B6478] leading-relaxed">{p.body}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ────────── PROJECTS GRID ────────── */}
      <section className="section-pad bg-white">
        <div className="container-x">
          <div className="grid lg:grid-cols-12 gap-10 mb-12 items-start">
            <div className="lg:col-span-7">
              <div className="eyebrow mb-3">Rotating snapshot</div>
              <h2 className="text-[30px] md:text-[40px] font-bold text-[#0E2A5C] leading-tight mb-4">
                Cases from the field.
              </h2>
              <p className="text-[#0B0F1A]/80 text-[15.5px] md:text-[16px] leading-relaxed mb-4">
                <span className="font-semibold text-[#0E2A5C]">Each case is unique — just like you.</span> A company with this longevity cannot limit one page showcase or the various sizes of client interface: from government and cross-government institutions, private and public commercial, residential and industrial properties. <span className="italic text-[#0E2A5C]">No job is too small.</span>
              </p>
              <p className="text-[#5B6478] text-[14.5px] leading-relaxed">
                A curated selection, updated periodically — augmented by our latest posts and reviews on Google Business Profile.
              </p>
            </div>

            {/* Sectors served — SEO breadth chip stack */}
            <div className="lg:col-span-5" data-testid="sectors-served">
              <div className="text-[10.5px] uppercase tracking-[0.24em] font-bold text-[#0E2A5C]/70 mb-3">
                Sectors we serve
              </div>
              <div className="flex flex-wrap gap-2">
                {SECTORS.map((s) => (
                  <span
                    key={s.label}
                    className="inline-flex items-center gap-1.5 bg-[#F7F8F3] border border-[#0E2A5C]/12 text-[#0E2A5C] text-[12px] font-semibold px-3 py-1.5 rounded-full hover:border-[#A6CD3C] hover:bg-white transition-colors"
                  >
                    <s.icon size={13} className="text-[#3B6CA8]" />
                    {s.label}
                  </span>
                ))}
              </div>
            </div>
          </div>

          {/* ────────── COMPLIANCE BAND — WHS Act 2011, Regs, Codes, ISO ────────── */}
          <div
            className="relative overflow-hidden rounded-2xl bg-gradient-to-r from-[#0E2A5C] via-[#0A1F47] to-[#0E2A5C] px-6 md:px-10 py-8 mb-14 border border-[#A6CD3C]/25 shadow-[0_24px_50px_-24px_rgba(14,42,92,0.45)]"
            data-testid="compliance-band"
          >
            <div className="absolute -top-16 -right-16 w-72 h-72 rounded-full bg-[#A6CD3C]/15 blur-3xl pointer-events-none" aria-hidden />
            <div className="relative grid lg:grid-cols-12 gap-6 items-center">
              <div className="lg:col-span-5">
                <div className="inline-flex items-center gap-2 bg-white/8 border border-white/15 rounded-full px-3 py-1 mb-3">
                  <span className="w-1.5 h-1.5 rounded-full bg-[#A6CD3C]" />
                  <span className="text-[10.5px] tracking-[0.24em] uppercase font-bold text-white/90">Work ethics & compliance</span>
                </div>
                <h3 className="text-white text-[22px] md:text-[26px] font-bold leading-tight">
                  Practical, cost-monitored{" "}
                  <span className="text-[#A6CD3C]">methodologies</span> —
                  dovetailing ISO compliance.
                </h3>
                <p className="text-white/70 text-[13.5px] leading-relaxed mt-3 max-w-xl">
                  Adherence to the WHS Act 2011, WHS Regulations and various Codes of Practice, underpinned by disciplined cost-monitoring and ISO-aligned quality systems on every engagement.
                </p>
              </div>
              <div className="lg:col-span-7 grid grid-cols-2 md:grid-cols-4 gap-3">
                {COMPLIANCE.map((c) => (
                  <div
                    key={c.code}
                    className="bg-white/5 backdrop-blur-sm border border-white/12 rounded-xl p-4 hover:border-[#A6CD3C]/60 transition-colors"
                    data-testid={`compliance-${c.code.toLowerCase().replace(/\s+/g, "-")}`}
                  >
                    <div className="w-9 h-9 rounded-lg bg-[#A6CD3C]/15 border border-[#A6CD3C]/30 flex items-center justify-center mb-2.5">
                      <c.icon size={16} className="text-[#A6CD3C]" />
                    </div>
                    <div className="text-white text-[12.5px] font-bold leading-tight">{c.code}</div>
                    <div className="text-white/55 text-[10.5px] mt-1 leading-snug">{c.label}</div>
                  </div>
                ))}
              </div>
            </div>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-7">
            {PROJECTS.map((p, i) => {
              const caseNum = String(i + 1).padStart(2, "0");
              const dotColor = EXPERTISE_DOT[p.expertise] || "bg-[#A6CD3C]";
              const gradients = [
                "from-[#0E2A5C] via-[#1A3870] to-[#0A1F47]",
                "from-[#0A1F47] via-[#0E2A5C] to-[#1E3A6E]",
                "from-[#1E3A6E] via-[#3B6CA8] to-[#0E2A5C]",
                "from-[#0E2A5C] via-[#0A1F47] to-[#1A3870]",
                "from-[#3B6CA8] via-[#1A3870] to-[#0E2A5C]",
                "from-[#0A1F47] via-[#1A3870] to-[#3B6CA8]",
                "from-[#0E2A5C] via-[#1E3A6E] to-[#0A1F47]",
                "from-[#1A3870] via-[#0E2A5C] to-[#0A1F47]",
                "from-[#0E2A5C] via-[#3B6CA8] to-[#1A3870]",
              ];
              return (
                <article
                  key={p.id}
                  className="bg-white rounded-2xl overflow-hidden border border-[#0E2A5C]/10 group transition-all hover:-translate-y-1.5 hover:shadow-[0_28px_56px_-22px_rgba(14,42,92,0.35)] hover:border-[#A6CD3C] flex flex-col"
                  data-testid={`project-card-${p.id}`}
                  itemScope
                  itemType="https://schema.org/Article"
                >
                  {/* IMAGE / PLACEHOLDER */}
                  <div className={`relative aspect-[4/3] overflow-hidden ${!p.image ? `bg-gradient-to-br ${gradients[i % gradients.length]}` : "bg-[#0A1F47]"}`}>
                    {p.image ? (
                      <img
                        src={p.image}
                        alt={`${p.title} — ${p.location}, ISGS Asbestos Consulting case study`}
                        className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-[1.04]"
                        style={p.rotate ? { transform: "rotate(90deg) scale(1.34)", transformOrigin: "center" } : undefined}
                        itemProp="image"
                        loading="lazy"
                      />
                    ) : (
                      <>
                        <div className="absolute inset-0 grid-overlay opacity-30" />
                        <div className="absolute inset-0 bg-gradient-to-t from-black/40 to-transparent" />
                        <div className="absolute inset-0 flex items-center justify-center">
                          <div className="w-14 h-14 rounded-full bg-white/8 border border-white/20 flex items-center justify-center backdrop-blur-sm">
                            <Camera size={20} className="text-white/70" />
                          </div>
                        </div>
                      </>
                    )}

                    {/* Case number badge — kept as requested */}
                    <div className="absolute top-4 left-4 bg-[#A6CD3C] text-[#0E2A5C] text-[10.5px] tracking-[0.18em] uppercase font-bold px-3 py-1 rounded-full shadow-md">
                      Case #{caseNum}
                    </div>

                    {/* Year badge */}
                    <div className="absolute top-4 right-4 bg-white/95 text-[#0E2A5C] text-[10.5px] tracking-[0.15em] uppercase font-bold px-2.5 py-1 rounded-full shadow-sm inline-flex items-center gap-1.5">
                      <Calendar size={11} />
                      {p.year}
                    </div>

                    {/* Bottom location strip */}
                    <div className="absolute bottom-0 inset-x-0 p-4 flex items-center gap-2 text-white">
                      <MapPin size={13} className="text-[#A6CD3C] shrink-0" />
                      <span className="text-[12.5px] font-medium truncate" itemProp="contentLocation">{p.location}</span>
                    </div>
                  </div>

                  {/* BODY */}
                  <div className="p-6 flex-1 flex flex-col">
                    <div className="flex items-center gap-2 mb-2.5">
                      <span className={`w-1.5 h-1.5 rounded-full ${dotColor}`} />
                      <span className="text-[10.5px] uppercase tracking-[0.22em] text-[#5B6478] font-bold" itemProp="about">
                        {p.category}
                      </span>
                    </div>
                    <h3 className="text-[17.5px] font-semibold text-[#0E2A5C] mb-2.5 leading-snug" itemProp="headline">
                      {p.title}
                    </h3>
                    <p className="text-[13.5px] text-[#5B6478] leading-relaxed mb-5" itemProp="description">
                      {p.summary}
                    </p>

                    {/* Service chips */}
                    <div className="mt-auto pt-4 border-t border-[#0E2A5C]/8">
                      <div className="text-[9.5px] uppercase tracking-[0.22em] text-[#5B6478] font-bold mb-2">
                        Services Delivered
                      </div>
                      <div className="flex flex-wrap gap-1.5">
                        {p.services.map((s) => (
                          <span
                            key={s}
                            className="text-[10.5px] font-semibold text-[#0E2A5C] bg-[#F7F8F3] border border-[#0E2A5C]/10 px-2.5 py-1 rounded-full"
                            itemProp="keywords"
                          >
                            {s}
                          </span>
                        ))}
                      </div>
                    </div>
                  </div>
                </article>
              );
            })}
          </div>
        </div>
      </section>
    </div>
  );
}
